import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { caseStudies, categories } from '../data/caseStudiesData';
import { ArrowRight } from 'lucide-react';

const CaseStudies = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredCaseStudies = activeCategory === 'All'
    ? caseStudies
    : caseStudies.filter(cs => cs.category.includes(activeCategory));

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Case Studies"
        description="Explore our portfolio of successful projects across fintech, real estate, SaaS, and more. See how we've helped companies build and scale their digital products."
        path="/case-studies"
      />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden" style={{ background: '#F8FAFC' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px]" style={{ background: 'radial-gradient(circle, rgba(38,147,255,0.06) 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest mb-4 font-heading" style={{ color: '#2693FF' }}>
            Our Work
          </p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 font-heading mb-12 leading-tight">
            Case Studies
          </h1>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'text-white'
                    : 'bg-white text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300'
                }`}
                style={activeCategory === category ? { background: '#2693FF' } : {}}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCaseStudies.map((study) => (
              <div
                key={study.id}
                className="card-lift group bg-white rounded-2xl overflow-hidden border border-slate-100 flex flex-col"
                style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-xl font-bold text-slate-900 font-heading mb-2 group-hover:text-[#2693FF] transition-colors">
                    {study.title}
                  </h3>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {study.category.map((cat, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full font-medium"
                        style={{ background: 'rgba(38,147,255,0.08)', color: '#2693FF' }}
                      >
                        {cat}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-slate-500 mb-6 leading-relaxed flex-1">
                    {study.description}
                  </p>

                  <Link
                    to={`/case-studies/${study.slug}`}
                    className="btn-gradient inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-medium group/btn self-start"
                  >
                    <span>View Case Study</span>
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* No Results Message */}
          {filteredCaseStudies.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-400 text-lg">
                No case studies found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CaseStudies;
